import { ReactNode } from "react";
import { Box, Paper, Typography } from "@mui/material";

import { InfoButton } from "../../components/Tooltips";

/** Every plot area on the page is this tall, so charts sitting side by side line up. */
export const CHART_HEIGHT = 300;

/** Room for a title plus a two-line subtitle: without it, a longer caption on one card
 *  pushes its plot area down and the row stops being readable as a comparison. */
const HEADER_MIN_HEIGHT = 62;

/**
 * Shared frame for the charts. Width comes from the ChartRow grid; what this owns is the
 * vertical rhythm — a fixed header height and a fixed plot height — so the axes of two cards
 * sitting side by side line up.
 */
const ChartCard = ({
  title,
  subtitle,
  help,
  children,
}: {
  title: string;
  subtitle?: ReactNode;
  help?: ReactNode;
  children: ReactNode;
}) => (
  <Paper
    elevation={1}
    sx={{
      p: 2,
      // The row is a grid: it owns the column width. minWidth 0 lets this card shrink
      // below its content instead of widening its column.
      minWidth: 0,
      display: "flex",
      flexDirection: "column",
    }}
  >
    <Box sx={{ minHeight: HEADER_MIN_HEIGHT }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
        <Typography sx={{ fontWeight: 600 }}>{title}</Typography>
        {help && <InfoButton title={help} translateTitle={false} />}
      </Box>
      {subtitle && (
        <Typography
          variant="caption"
          sx={{ color: "text.secondary", display: "block" }}
        >
          {subtitle}
        </Typography>
      )}
    </Box>

    <Box sx={{ mt: 2, flexGrow: 1, minHeight: CHART_HEIGHT }}>{children}</Box>
  </Paper>
);

export default ChartCard;
